/**
 * Dettaglio dei numeri del gruppo "Servizi" nel pannello comune (con Excel): le query con ESATTAMENTE i criteri
 * di salesTargetStore (offerte accettate con signed_at nel periodo, persona = assigned_to, una o piu' persone)
 * e la costruzione delle DetailRequest. Le regole pure stanno in servicesDetailRows.ts.
 */
import { supabase } from '../../supabase';
import { useSettingsStore } from '../../stores/settingsStore';
import { fetchAllPages } from '../fetchAllPages';
import { applyScope } from '../personScope';
import { trendWindow } from '../serviceTrends';
import type { DetailRequest, DetailResult } from './types';
import {
  FIC_COLUMNS, SERVICE_COLUMNS, fileNameOf, ficLinesResult, filterFic, forPerson, forTopService, forTrend, inCategories,
  periodLabelIt, serviceLinesResult, toFicLine, toServiceLine,
  type FicLine, type ServiceLine, type TrendKind,
} from './servicesDetailRows';

/** Cio' che serve per rifare gli stessi conti dei widget: periodo, persone, finestra dei trend. */
export interface ServicesScope {
  organizationId: string;
  /** AAAA-MM-GG, inizio incluso e fine ESCLUSA (come le query dello store). */
  monthStart: string;
  monthEnd: string;
  /** [] = tutto il team. */
  userIds: string[];
  /** Istante e numero di mesi della finestra dei trend (ultimi N mesi). */
  trendNow: number;
  trendMonths: number;
}

let current: ServicesScope | null = null;
/** Lo store ricorda con quali filtri ha caricato i grafici dei servizi, cosi' il dettaglio usa gli stessi. */
export const rememberServicesScope = (s: ServicesScope): void => { current = s; };
export const getServicesScope = (): ServicesScope | null => current;

const fail = (error: unknown): never => {
  throw new Error((error as { message?: string })?.message || 'Impossibile caricare il dettaglio');
};

const LINE_SELECT = (inner: boolean) => `
  id, service_id, custom_service_name, amount, discounted_amount, monthly_duration,
  services${inner ? '!inner' : ''} (title, service_type, category_id, service_categories (id, name, color)),
  deals!inner (
    id, title, status, signed_at, organization_id, assigned_to, client_id,
    clients (business_name),
    user_profiles!deals_assigned_to_fkey (full_name, email)
  )`;

/** Righe servizio delle offerte accettate firmate tra from (incluso) e to (escluso). Le ultime due: servizio obbligatorio come nei grafici. */
export async function fetchServiceLines(org: string, from: string, to: string, userIds: readonly string[], requireService: boolean): Promise<ServiceLine[]> {
  const { data, error } = await fetchAllPages((a, b) => applyScope(supabase
    .from('deal_services')
    .select(LINE_SELECT(requireService))
    .eq('deals.organization_id', org)
    .eq('deals.status', 'accepted')
    .gte('deals.signed_at', from)
    .lt('deals.signed_at', to), 'deals.assigned_to', userIds)
    .order('id')
    .range(a, b));
  if (error) fail(error);
  return (data || []).map(toServiceLine);
}

/** Come shouldUseFicRevenue dello store: l'azienda ha scelto "fatturato" e Fatture in Cloud e' collegato. */
async function usesFicRevenue(org: string): Promise<boolean> {
  if (useSettingsStore.getState().settings?.dashboard_revenue_source !== 'invoiced') return false;
  // Ricordata per un minuto: la scelta serve prima di aprire il pannello (decide le colonne) e di nuovo al caricamento
  const hit = ficCache.get(org);
  if (hit && Date.now() - hit.at < 60_000) return hit.value;
  const { data } = await supabase.from('fattureincloud_configs').select('is_connected').eq('organization_id', org).maybeSingle();
  const value = data?.is_connected === true;
  ficCache.set(org, { at: Date.now(), value });
  return value;
}
const ficCache = new Map<string, { at: number; value: boolean }>();

/** Fatture e note di credito del periodo (stessa lettura di fetchFicInvoices dello store, con offerta, cliente e servizio). */
async function fetchFicLines(org: string, from: string, to: string): Promise<FicLine[]> {
  const { data, error } = await fetchAllPages((a, b) => supabase
    .from('fattureincloud_invoices')
    .select(`
      id, number, net_amount, type, date, deal_id, deal_service_id,
      deals (title, client_id, assigned_to, clients (business_name), user_profiles!deals_assigned_to_fkey (full_name, email)),
      deal_services (service_id, custom_service_name, services (title, category_id, service_categories (id, name, color)))
    `)
    .eq('organization_id', org)
    .in('type', ['invoice', 'credit_note'])
    .neq('status', 'cancelled')
    .gte('date', from)
    .lt('date', to)
    .order('id')
    .range(a, b));
  if (error) fail(error);
  return (data || []).map(toFicLine);
}

/** Nomi delle persone del filtro, per il sottotitolo. */
async function personNames(ids: readonly string[]): Promise<string> {
  if (ids.length === 0) return '';
  const { data } = await supabase.from('user_profiles').select('full_name, email').in('id', [...ids]);
  return (data || []).map((u: any) => u.full_name || u.email).filter(Boolean).join(', ');
}

const withPeople = async (scope: ServicesScope, extra?: string): Promise<string | undefined> => {
  const names = await personNames(scope.userIds);
  return [names && `persone: ${names}`, extra].filter(Boolean).join(', ') || undefined;
};

const periodOf = (s: ServicesScope) => periodLabelIt(s.monthStart, s.monthEnd);
const scopeKey = (s: ServicesScope) => [s.organizationId, s.monthStart, s.monthEnd, [...s.userIds].sort().join(',')];

/** Categorie e "Chi vende cosa": righe del periodo oppure, in modalita' fatturato, le fatture. */
async function loadByCategories(scope: ServicesScope, categoryIds: string[] | null, userId?: string): Promise<DetailResult> {
  const who = await withPeople(scope);
  if (await usesFicRevenue(scope.organizationId)) {
    const all = await fetchFicLines(scope.organizationId, scope.monthStart, scope.monthEnd);
    // Chi vende cosa salta le fatture di offerte senza assegnato (non attribuibili), come lo store
    return ficLinesResult(filterFic(all, scope.userIds, categoryIds, userId), who);
  }
  const all = await fetchServiceLines(scope.organizationId, scope.monthStart, scope.monthEnd, scope.userIds, true);
  const lines = userId ? forPerson(all, userId, categoryIds) : inCategories(all, categoryIds);
  return serviceLinesResult(lines, who);
}

const columnsFor = (fic: boolean) => (fic ? FIC_COLUMNS : SERVICE_COLUMNS);

/**
 * Le colonne dipendono dalla fonte (fatture o righe servizio), nota solo al caricamento: il pannello legge le colonne
 * dalla richiesta, quindi la fonte si decide qui, prima di aprire (una lettura veloce di due impostazioni).
 */
async function sourceColumns(org: string) {
  return columnsFor(await usesFicRevenue(org));
}

/** Categoria (o tutte, o "Altro" = elenco di categorie) del grafico "Cosa vendete di piu'". */
export async function categoryDetailRequest(scope: ServicesScope, categoryIds: string[] | null, label: string): Promise<DetailRequest> {
  const period = periodOf(scope);
  const title = categoryIds ? `Vendite in ${label}` : 'Vendite di tutte le categorie';
  return {
    key: JSON.stringify(['cat', ...scopeKey(scope), categoryIds]),
    title, subtitle: period, fileName: fileNameOf(title, period),
    columns: await sourceColumns(scope.organizationId),
    load: () => loadByCategories(scope, categoryIds),
  };
}

/** Cella persona x categoria (o tutte le categorie della persona) di "Chi vende cosa". */
export async function personCategoryDetailRequest(scope: ServicesScope, userId: string, userName: string, categoryIds: string[] | null, categoryLabel: string): Promise<DetailRequest> {
  const period = periodOf(scope);
  const title = categoryIds ? `${userName}: ${categoryLabel}` : `${userName}: tutte le categorie`;
  return {
    key: JSON.stringify(['pcat', ...scopeKey(scope), userId, categoryIds]),
    title, subtitle: period, fileName: fileNameOf(title, period),
    columns: await sourceColumns(scope.organizationId),
    load: () => loadByCategories(scope, categoryIds, userId),
  };
}

/** Un mese (AAAA-MM) o tutta la finestra, per serie: ricorrenti, una tantum, totale. */
export function trendDetailRequest(scope: ServicesScope, monthKey: string | null, monthLabel: string, kind: TrendKind): DetailRequest {
  const seriesLabel = { recurring: 'ricorrenti', one_time: 'una tantum', total: 'vendute' }[kind];
  const title = `Servizi ${seriesLabel}: ${monthLabel}`;
  const period = monthKey ? '' : `ultimi ${scope.trendMonths} mesi`;
  return {
    key: JSON.stringify(['trend', scope.organizationId, [...scope.userIds].sort().join(','), scope.trendNow, scope.trendMonths, monthKey, kind]),
    title, subtitle: period || undefined, fileName: fileNameOf(title, period),
    columns: SERVICE_COLUMNS,
    load: async () => {
      // Stessa finestra del grafico: dall'inizio del primo mese all'inizio del mese dopo l'ultimo, ora locale
      let start: Date, end: Date;
      if (monthKey) {
        const [y, m] = monthKey.split('-').map(Number);
        start = new Date(y, m - 1, 1);
        end = new Date(y, m, 1);
      } else {
        ({ startDate: start, endDate: end } = trendWindow(new Date(scope.trendNow), scope.trendMonths));
      }
      const all = await fetchServiceLines(scope.organizationId, start.toISOString(), end.toISOString(), scope.userIds, true);
      return serviceLinesResult(forTrend(all, monthKey, kind), await withPeople(scope));
    },
  };
}

/** Un servizio di "Servizi piu' venduti" (anche una voce senza servizio collegato). */
export function topServiceDetailRequest(scope: ServicesScope, groupId: string, name: string): DetailRequest {
  const period = periodOf(scope);
  const title = `Servizio: ${name}`;
  return {
    key: JSON.stringify(['top', ...scopeKey(scope), groupId]),
    title, subtitle: period, fileName: fileNameOf(title, period),
    columns: SERVICE_COLUMNS,
    load: async () => {
      // Anche le righe senza servizio collegato (prodotti, nome libero): servizio NON obbligatorio, come nello store
      const all = await fetchServiceLines(scope.organizationId, scope.monthStart, scope.monthEnd, scope.userIds, false);
      return serviceLinesResult(forTopService(all, groupId), await withPeople(scope));
    },
  };
}
